import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC } from 'react';
import Banner from '@/components/UI/Banner';
import { useForm, Controller } from 'react-hook-form';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import { useNavigate } from 'react-router-dom';
import { RouteService } from '@/support/RouteService.ts';
import { _isNil } from '@/littledash.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { type AnimalApiId } from '@/model/Animal.model.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { type Study } from '@/model/Study.model.ts';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import { type Cage } from '@/model/Cage.model.ts';
import Loading from '@/components/Loading';

interface MoveToStudyConfirmationV2Props {
  targetStudy: Study;
  sourceStudy: Study;
  animal_api_ids: AnimalApiId[];
  animalCageNames: Set<string>;
  closeModal: () => void;
}

type MoveToStudyConfirmationFormProps = Omit<MoveToStudyConfirmationV2Props, 'animalCageNames'> & {
  duplicateCageNames: boolean;
};

export const MoveToStudyConfirmationV2: FC<MoveToStudyConfirmationV2Props> = ({ animalCageNames, ...props }) => {
  const { response: cagesResponse, loading: cageLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.cages.list',
    path: { studyId: props.targetStudy.id },
    options: { onError: { toast: true, slug: 'study-cages-load' } },
  });

  if (cageLoading || _isNil(cagesResponse)) {
    return <Loading txt="Loading move animals confirmation" />;
  }

  const targetCages = cagesResponse.body as { data: Cage[] };
  const duplicateCageNames = targetCages?.data.some((cage) => animalCageNames.has(cage.name)) ?? false;

  return <MoveToStudyConfirmationForm {...props} duplicateCageNames={duplicateCageNames} />;
};

/**
 * Mounted only once the target study's cages have loaded, so the form starts from the loaded
 * duplicate-cage default.
 */
const MoveToStudyConfirmationForm: FC<MoveToStudyConfirmationFormProps> = ({
  targetStudy,
  sourceStudy,
  animal_api_ids,
  duplicateCageNames,
  closeModal,
}) => {
  const { handleSubmit, control, getValues, formState } = useForm({
    defaultValues: { duplicateCageNames },
  });

  const { invoke: postAnimalsMove, loading: postingMoveAnimals } = useApiHook({
    endpoint: 'POST /api/v1/operations/study-animal-move',
    body: {
      old_study_api_id: sourceStudy.api_id,
      new_study_api_id: targetStudy.api_id,
      animal_api_ids: animal_api_ids,
      variables: undefined,
    },
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: true, slug: 'move-animals' } },
  });

  const navigate = useNavigate();

  const submit = async () => {
    try {
      await postAnimalsMove({
        body: {
          old_study_api_id: sourceStudy.api_id,
          new_study_api_id: targetStudy.api_id,
          animal_api_ids: animal_api_ids,
          variables: undefined,
        },
      });
      closeModal();
      // Read the checkbox at success time so the user can change the choice
      // while the (potentially long-running) move is still in flight.
      if (getValues('duplicateCageNames')) {
        navigate(
          RouteService.web({
            route: 'studies.cages',
            path: { id: targetStudy.id },
          }).route
        );
      } else {
        navigate(
          RouteService.web({
            route: 'studies.show',
            path: { id: sourceStudy.id },
          }).route
        );
      }
      successToast('Animals moved successfully');
    } catch (err) {
      const ivError = new InVivoError('Could not move animals', {
        slug: 'move-animals',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not move animals');
    }
  };

  const isMoving = formState.isSubmitting || postingMoveAnimals;

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader title="Move animals to study?" className="pa3 bb b--moon-gray w-100" />
      <div className="pl3 pr3 mv3 near-black" data-testid="confirm-info-container">
        <p className="pb3">
          Samples, observations and dosing will remain intact.{' '}
          <span className="fw5">All groups, alerts or tracking dates will be permanently removed.</span>{' '}
          <span className="red">This cannot be undone.</span>
        </p>
        {duplicateCageNames && (
          <Banner testId="duplicate-cages-banner" info dismiss={false} className="mw6 mb3">
            <h3 className="normal lh-title f5 pb2" data-testid="metadata-settings__empty-banner--title">
              There will be duplicate cage names in the recipient study. <span className="fw5">Update the names</span>{' '}
              in the new study to ensure uniqueness.
            </h3>
          </Banner>
        )}
        <form onSubmit={handleSubmit(submit)}>
          <div className="mt3">
            <Controller
              name="duplicateCageNames"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  testPrefix="duplicate-cages-check"
                  name="duplicate-cages"
                  label="Take me to the new study"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
        </form>
      </div>
      <ModalActions
        submitButtonProps={{ loading: isMoving, loadingText: 'Moving animals' }}
        cancelButtonProps={{ disabled: isMoving }}
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(submit)}
        onCancel={closeModal}
        submitBtnText="Confirm"
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
